// =============================================================================
// Auth Store — Zustand store for authentication state
// =============================================================================
// Manages the current user session, auth status, and provides actions
// for login, logout, and session initialization.
//
// The actual JWT tokens are in HttpOnly cookies (managed by the server).
// This store only holds the user profile and session metadata.
// =============================================================================

import { create } from 'zustand';
import type { AuthUser, LoginRequest, RegisterRequest } from '@tkpod/shared';
import { api, startSessionKeeper, stopSessionKeeper } from './api';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  /** Current authentication status */
  status: AuthStatus;
  /** Authenticated user profile (null when unauthenticated) */
  user: AuthUser | null;
  /** Error message from last auth operation */
  error: string | null;
}

interface AuthActions {
  /**
   * Initialize auth state on app startup.
   * Calls GET /auth/me to check if the user has a valid session.
   */
  initialize: () => Promise<void>;

  /**
   * Re-fetch /auth/me and update user state.
   * Use after org settings change, role change, etc.
   * Silent failure — keeps current state on error.
   */
  reinitialize: () => Promise<void>;

  /**
   * Log in with email and password.
   * Sets HttpOnly cookies on success (handled by server).
   */
  login: (credentials: LoginRequest) => Promise<void>;

  /**
   * Register a new account.
   * After successful registration, hydrates user state via /me to confirm cookie round-trip.
   */
  register: (data: RegisterRequest) => Promise<void>;

  /**
   * Log out — clears server session and local state.
   */
  logout: () => Promise<void>;

  /**
   * Switch the active organization for this session.
   * The server validates membership, rotates tokens and returns the
   * updated user with the new currentOrganization.
   */
  switchOrganization: (organizationId: string) => Promise<void>;

  /**
   * Update the stored user profile (e.g., after profile edit).
   */
  setUser: (user: AuthUser) => void;

  /**
   * Clear any error message.
   */
  clearError: () => void;
}

export type AuthStore = AuthState & AuthActions;

// Module-level promise to deduplicate concurrent initialize() calls (StrictMode, etc.)
let initPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthStore>((set, get) => ({
  // --- State ---
  status: 'idle',
  user: null,
  error: null,

  // --- Actions ---

  initialize: async () => {
    // Already resolved or in flight — skip
    if (get().status !== 'idle') return;
    if (initPromise) return initPromise;

    initPromise = (async () => {
      set({ status: 'loading', error: null });
      try {
        // noRetry: /auth/me returns 200 { user: null } for unauthenticated,
        // so 401 won't occur. noRetry is a safety net to prevent refresh loops
        // if the endpoint contract ever changes.
        const response = await api.get<{ user: AuthUser | null }>('/api/v1/auth/me', {
          noRetry: true,
        });

        // A parallel flow (e.g. SSO consume on /sso/shopify) may have
        // authenticated the user while our /auth/me call was in flight.
        // Never downgrade an already-authenticated state here.
        if (get().status === 'authenticated') return;

        if (response.user) {
          set({ status: 'authenticated', user: response.user, error: null });
          startSessionKeeper();
        } else {
          set({ status: 'unauthenticated', user: null, error: null });
        }
      } catch {
        // Same guard — don't clobber authenticated state on a transient
        // network error during initialization.
        if (get().status === 'authenticated') return;
        set({ status: 'unauthenticated', user: null, error: null });
      }
    })();

    return initPromise;
  },

  reinitialize: async () => {
    try {
      const response = await api.get<{ user: AuthUser | null }>('/api/v1/auth/me', {
        noRetry: true,
      });
      if (response.user) {
        set({ status: 'authenticated', user: response.user, error: null });
      } else {
        set({ status: 'unauthenticated', user: null, error: null });
      }
    } catch {
      // Silent failure — keep current state
    }
  },

  login: async (credentials: LoginRequest) => {
    set({ status: 'loading', error: null });
    try {
      const response = await api.post<{ user: AuthUser }>('/api/v1/auth/login', credentials, {
        noRetry: true,
      });
      set({ status: 'authenticated', user: response.user, error: null });
      startSessionKeeper();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Login failed. Please try again.';
      set({ status: 'unauthenticated', user: null, error: message });
      throw err;
    }
  },

  register: async (data: RegisterRequest) => {
    set({ status: 'loading', error: null });
    try {
      // Register sets HttpOnly cookies on success
      await api.post('/api/v1/auth/register', data, { noRetry: true });

      // Hydrate user state via /me to confirm cookie round-trip works
      const meResponse = await api.get<{ user: AuthUser }>('/api/v1/auth/me');
      set({ status: 'authenticated', user: meResponse.user, error: null });
      startSessionKeeper();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Registration failed. Please try again.';
      set({ status: 'unauthenticated', user: null, error: message });
      throw err;
    }
  },

  switchOrganization: async (organizationId: string) => {
    const response = await api.post<{ user: AuthUser }>(
      '/api/v1/auth/switch-organization',
      { organizationId },
      { noRetry: true },
    );
    set({ status: 'authenticated', user: response.user, error: null });
  },

  logout: async () => {
    stopSessionKeeper();
    try {
      await api.post('/api/v1/auth/logout', undefined, { noRetry: true });
    } catch {
      // Logout API failure is non-critical — clear local state anyway
    }
    set({ status: 'unauthenticated', user: null, error: null });
  },

  setUser: (user: AuthUser) => {
    set({ user, status: 'authenticated' });
  },

  clearError: () => {
    set({ error: null });
  },
}));
